import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router';
import { Game, End, Shot, RockColor } from '../types/curling';
import { getGame, saveGame, getTeams, SavedTeam, saveTeam, getClubs, saveClub } from '../utils/storage';
import { searchSavedTeams, SavedTeamSearchResult } from '../api/savedTeams';
import { getPointsFromResult } from '../utils/analytics';
import { ShotTracker, Rock } from '../components/ShotTracker';
import { ShotPlanView } from '../components/ShotPlanView';
import { SheetVisualizer } from '../components/SheetVisualizer';
import { Play, CheckCircle2, Trash2, Plus, Flag, UserPlus, X, Lock, Repeat2, ArrowLeft, Eye, EyeOff, Globe, Building2, Save, Star, Bomb, Map, MapPin, List, Target, ChevronDown, ChevronUp, Users, Gauge } from 'lucide-react';
import { curlingClubs, CurlingClub } from '../data/curlingClubs';

// Helper to get zone label from position
const getZoneLabel = (position: number): string => {
  const zones = [
    'High Guard', 'Mid Guard', 'Tight Guard', 'Top Twelve', 'Top Eight',
    'Top Four', 'Tee Line', 'Back Four', 'Back Eight', 'Back Twelve',
    'Hack', 'Board', 'Control', 'Normal', 'Firm'
  ];
  return zones[position - 1] || 'Unknown';
};

// Helper to convert RockColor to hex color
const getRockColorHex = (color?: RockColor): string => {
  const colorMap: Record<RockColor, string> = {
    yellow: '#fbbf24',
    green: '#10b981',
    blue: '#3b82f6',
    red: '#ef4444',
    white: '#f3f4f6',
  };
  return color ? colorMap[color] : '#ef4444';
};

export function LiveGame() {
  const navigate = useNavigate();
  const location = useLocation();
  const { gameId } = useParams<{ gameId: string }>();
  const [game, setGame] = useState<Game | null>(null);
  const [showShotTracker, setShowShotTracker] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState<'team1' | 'team2'>('team1');
  const [selectedEndNumber, setSelectedEndNumber] = useState(1);
  const [showSetup, setShowSetup] = useState(true);
  const [savedTeams, setSavedTeams] = useState<SavedTeam[]>([]);
  const [rockPositionsByEnd, setRockPositionsByEnd] = useState<Record<number, Rock[]>>({});
  const [showRockOrderPopup, setShowRockOrderPopup] = useState(false);
  const [rockOrderTeam, setRockOrderTeam] = useState<'team1' | 'team2'>('team1');
  const [collapsedEnds, setCollapsedEnds] = useState<Set<number>>(new Set());

  // Setup form state
  const [curlingClub, setCurlingClub] = useState('');
  const [sheetNumber, setSheetNumber] = useState('');
  const [team1Name, setTeam1Name] = useState('');
  const [team2Name, setTeam2Name] = useState('');
  const [numberOfEnds, setNumberOfEnds] = useState(8);
  const [team1Players, setTeam1Players] = useState<string[]>(['', '']);
  const [team2Players, setTeam2Players] = useState<string[]>(['', '']);
  const [team1RockColor, setTeam1RockColor] = useState<RockColor>('red');
  const [team2RockColor, setTeam2RockColor] = useState<RockColor>('blue');
  const [startsWithHammer, setStartsWithHammer] = useState<'team1' | 'team2' | 'lsd'>('team1');
  const [expectedTimeTeeLine, setExpectedTimeTeeLine] = useState(14.5);
  const [expectedTimeHack, setExpectedTimeHack] = useState(12.5);
  const [expectedTimeBoard, setExpectedTimeBoard] = useState(11.5);
  const [expectedTimeControl, setExpectedTimeControl] = useState(10.5);
  const [expectedTimeNormal, setExpectedTimeNormal] = useState(9.0);
  const [expectedTimeFirm, setExpectedTimeFirm] = useState(8.0);
  const [expectedTimePeel, setExpectedTimePeel] = useState(7.0);
  // Same last-resort default as DEFAULT_CURL_FEET in utils/teeLineEstimate,
  // pre-filled here so a game always carries an explicit expectedCurl rather
  // than relying on that fallback unless the setup form is actually skipped.
  const [defaultExpectedCurl, setDefaultExpectedCurl] = useState(4.75);
  
  // Autocomplete state
  const [showTeam1Suggestions, setShowTeam1Suggestions] = useState(false);
  const [showTeam2Suggestions, setShowTeam2Suggestions] = useState(false);
  const [showClubSuggestions, setShowClubSuggestions] = useState(false);
  const [clubSearchText, setClubSearchText] = useState('');

  // Which SavedTeam (own or another program's, via search) team1/2's name/players were
  // copied from, if any — cleared as soon as the name no longer matches that selection.
  // Saved onto the game as an identity snapshot, not a live reference.
  const [team1Source, setTeam1Source] = useState<{ accountId: string; teamId: string } | null>(null);
  const [team2Source, setTeam2Source] = useState<{ accountId: string; teamId: string } | null>(null);

  // Cross-program search results for the opponent-lookup section of each dropdown.
  const [otherProgramResults1, setOtherProgramResults1] = useState<SavedTeamSearchResult[]>([]);
  const [otherProgramResults2, setOtherProgramResults2] = useState<SavedTeamSearchResult[]>([]);
  
  // Add club modal state
  const [showAddClubModal, setShowAddClubModal] = useState(false);
  const [newClubName, setNewClubName] = useState('');
  const [newClubCity, setNewClubCity] = useState('');
  const [newClubCountry, setNewClubCountry] = useState('');
  const [customClubs, setCustomClubs] = useState<CurlingClub[]>([]);
  
  // Refs for auto-focusing new player inputs
  const team1InputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const team2InputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (!gameId || gameId === 'new') return;
    let cancelled = false;

    getGame(gameId)
      .then(existingGame => {
        if (cancelled || !existingGame) return;
        setGame(existingGame);
        setShowSetup(false);
        // Collapse all completed ends by default
        const completedEnds = existingGame.ends
          .filter(e => e.completed)
          .map(e => e.number);
        setCollapsedEnds(new Set(completedEnds));
      })
      .catch(error => console.error('Failed to load game:', error));

    return () => {
      cancelled = true;
    };
  }, [gameId]);

  useEffect(() => {
    let cancelled = false;

    Promise.all([getTeams(), getClubs()])
      .then(([teams, clubs]) => {
        if (cancelled) return;
        setSavedTeams(teams);
        setCustomClubs(clubs);
      })
      .catch(error => console.error('Failed to load teams and clubs:', error));

    return () => {
      cancelled = true;
    };
  }, []);

  // Debounced cross-program search, one per team-name field. A 300ms pause after the
  // last keystroke, and a 2-character floor matching the server's own, keep this from
  // hitting the network on every keystroke.
  useEffect(() => {
    if (team1Name.trim().length < 2) {
      setOtherProgramResults1([]);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      searchSavedTeams(team1Name)
        .then(results => { if (!cancelled) setOtherProgramResults1(results); })
        .catch(error => console.error('Failed to search other programs:', error));
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [team1Name]);

  useEffect(() => {
    if (team2Name.trim().length < 2) {
      setOtherProgramResults2([]);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      searchSavedTeams(team2Name)
        .then(results => { if (!cancelled) setOtherProgramResults2(results); })
        .catch(error => console.error('Failed to search other programs:', error));
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [team2Name]);

  // Auto-populate Hack and Board times based on Tee Line
  useEffect(() => {
    setExpectedTimeHack(expectedTimeTeeLine - 2);
    setExpectedTimeBoard(expectedTimeTeeLine - 3);
  }, [expectedTimeTeeLine]);
  
  // Auto-populate Control time based on Board
  useEffect(() => {
    const defaultControl = 10.5;
    if (expectedTimeBoard < defaultControl) {
      setExpectedTimeControl(expectedTimeBoard - 1);
    } else {
      setExpectedTimeControl(defaultControl);
    }
  }, [expectedTimeBoard]);
  
  // Auto-populate Normal time based on Control
  useEffect(() => {
    const defaultNormal = 9.5;
    if (expectedTimeControl < defaultNormal) {
      setExpectedTimeNormal(expectedTimeControl - 1);
    } else {
      setExpectedTimeNormal(defaultNormal);
    }
  }, [expectedTimeControl]);
  
  // Auto-populate Firm time based on Normal
  useEffect(() => {
    const defaultFirm = 8.5;
    if (expectedTimeNormal < defaultFirm) {
      setExpectedTimeFirm(expectedTimeNormal - 1);
    } else {
      setExpectedTimeFirm(defaultFirm);
    }
  }, [expectedTimeNormal]);
  
  // Get all clubs (default + custom)
  const getAllClubs = () => {
    return [...curlingClubs, ...customClubs].sort((a, b) => a.name.localeCompare(b.name));
  };

  const handleAddNewClub = async () => {
    if (!newClubName.trim() || !newClubCity.trim() || !newClubCountry.trim()) {
      alert('Please fill in all club fields');
      return;
    }

    const newClub: CurlingClub = {
      id: Date.now().toString(),
      name: newClubName.trim(),
      city: newClubCity.trim(),
      country: newClubCountry.trim(),
    };

    try {
      await saveClub(newClub);
      setCustomClubs(await getClubs());
    } catch (error) {
      console.error('Failed to save club:', error);
      alert('Could not save the club. Please try again.');
      return;
    }

    // Set the new club as selected
    const fullClubName = `${newClub.name} - ${newClub.city}, ${newClub.country}`;
    setCurlingClub(fullClubName);
    setClubSearchText(fullClubName);
    
    // Reset form and close modal
    setNewClubName('');
    setNewClubCity('');
    setNewClubCountry('');
    setShowAddClubModal(false);
  };

  const handleStartGame = () => {
    if (!team1Name.trim() || !team2Name.trim()) return;
    
    if (!curlingClub.trim()) {
      alert('Please select a curling club');
      return;
    }
    
    if (!sheetNumber.trim()) {
      alert('Please enter a sheet number');
      return;
    }

    // Filter out empty player names
    const validTeam1Players = team1Players.filter(p => p.trim());
    const validTeam2Players = team2Players.filter(p => p.trim());

    if (validTeam1Players.length === 0 || validTeam2Players.length === 0) {
      alert('Please add at least one player for each team');
      return;
    }

    if (validTeam1Players.length < 2 || validTeam2Players.length < 2) {
      alert('Each team must have at least 2 players');
      return;
    }

    const newGame: Game = {
      id: Date.now().toString(),
      curlingClub: curlingClub.trim(),
      sheetNumber: sheetNumber.trim(),
      team1Name: team1Name.trim(),
      team2Name: team2Name.trim(),
      team1Players: validTeam1Players,
      team2Players: validTeam2Players,
      team1SourceAccountId: team1Source?.accountId ?? null,
      team1SourceTeamId: team1Source?.teamId ?? null,
      team2SourceAccountId: team2Source?.accountId ?? null,
      team2SourceTeamId: team2Source?.teamId ?? null,
      date: Date.now(),
      ends: Array.from({ length: numberOfEnds }, (_, i) => ({
        number: i + 1,
        team1Score: 0,
        team2Score: 0,
        shots: [],
      })),
      isComplete: false,
      team1TotalScore: 0,
      team2TotalScore: 0,
      team1RockColor: team1RockColor,
      team2RockColor: team2RockColor,
      // 'lsd' means the last-stone draw decides, so the game records no starting
      // hammer and the LSD result is consulted instead (see getHammerTeam below).
      startsWithHammer: startsWithHammer === 'lsd' ? undefined : startsWithHammer,
      expectedTimes: {
        hack: expectedTimeHack,
        board: expectedTimeBoard,
        control: expectedTimeControl,
        normal: expectedTimeNormal,
        firm: expectedTimeFirm,
        peel: expectedTimePeel,
        teeLine: expectedTimeTeeLine,
      },
      expectedCurl: defaultExpectedCurl,
    };

    setGame(newGame);
    saveGame(newGame);
    setShowSetup(false);
    navigate(`/CurlingCodex/game/${newGame.id}`, { replace: true });
  };

  const handleShotRecorded = (shotData: Omit<Shot, 'id' | 'timestamp'>, updatedRocks: Rock[]) => {
    if (!game) return;

    const updatedGame = { ...game };
    
    // Create new shot
    const newShot: Shot = {
      ...shotData,
      id: Date.now().toString(),
      timestamp: Date.now(),
    };

    const currentEndData = updatedGame.ends.find(e => e.number === shotData.endNumber);
    
    if (currentEndData) {
      currentEndData.shots.push(newShot);
    }

    // Update rock positions for this end
    setRockPositionsByEnd(prev => ({
      ...prev,
      [shotData.endNumber]: updatedRocks,
    }));

    setGame(updatedGame);
    saveGame(updatedGame);
    
    // Determine if there are more shots in this end
    // Mixed doubles: 5 shots per team (10 total) - 2 rocks are pre-positioned
    // Other formats: 8 shots per team (16 total)
    const isMixedDoubles = game.team1Players.length === 2 && game.team2Players.length === 2;
    const shotsPerTeam = isMixedDoubles ? 5 : 8;
    const totalShotsInEnd = shotsPerTeam * 2;
    
    if (currentEndData && currentEndData.shots.length < totalShotsInEnd) {
      // There are more shots - determine next team and keep tracker open
      const lastShot = currentEndData.shots[currentEndData.shots.length - 1];
      const nextTeam = lastShot.team === 'team1' ? 'team2' : 'team1';
      
      // Update selected team for next shot
      setSelectedTeam(nextTeam);
      setSelectedEndNumber(shotData.endNumber);
      // showShotTracker remains true, so it will automatically show the next shot tracker
    } else {
      // End is complete, close the tracker
      setShowShotTracker(false);
    }
  };

  const handleEndScoreUpdate = (endNumber: number, team: 'team1' | 'team2', score: number) => {
    if (!game) return;

    const updatedGame = { ...game };
    const end = updatedGame.ends.find(e => e.number === endNumber);
    
    if (end) {
      if (team === 'team1') {
        end.team1Score = score;
      } else {
        end.team2Score = score;
      }

      // Recalculate totals
      updatedGame.team1TotalScore = updatedGame.ends.reduce((sum, e) => sum + e.team1Score, 0);
      updatedGame.team2TotalScore = updatedGame.ends.reduce((sum, e) => sum + e.team2Score, 0);
    }

    setGame(updatedGame);
    saveGame(updatedGame);
  };

  const handleCompleteGame = () => {
    if (!game) return;

    const updatedGame = { ...game, isComplete: true };
    setGame(updatedGame);
    saveGame(updatedGame);
    navigate('/CurlingCodex/history');
  };

  const handleDeleteShot = (endNumber: number, shotId: string) => {
    if (!game) return;

    const updatedGame = { ...game };
    const endData = updatedGame.ends.find(e => e.number === endNumber);
    
    if (endData) {
      const shotIndex = endData.shots.findIndex(s => s.id === shotId);
      if (shotIndex !== -1) {
        // Mark shot as skipped instead of deleting it
        endData.shots[shotIndex] = {
          ...endData.shots[shotIndex],
          skipped: true,
        };
      }
    }

    setGame(updatedGame);
    saveGame(updatedGame);
  };

  // Handler for marking an end as complete
  const handleCompleteEnd = (endNumber: number) => {
    if (!game) return;

    const updatedGame = { ...game };
    const end = updatedGame.ends.find(e => e.number === endNumber);

    if (end) {
      end.completed = true;
    }

    // The game isn't over just because the pre-set number of ends ran out — a tied
    // game plays extra ends. As long as the game hasn't been completed, finishing
    // the last recorded end always opens up the next one.
    const lastEndNumber = Math.max(...updatedGame.ends.map(e => e.number));
    if (!updatedGame.isComplete && endNumber === lastEndNumber) {
      updatedGame.ends = [
        ...updatedGame.ends,
        { number: lastEndNumber + 1, team1Score: 0, team2Score: 0, shots: [] },
      ];
    }

    setGame(updatedGame);
    saveGame(updatedGame);
    
    // Collapse all completed ends
    const allCompletedEnds = updatedGame.ends
      .filter(e => e.completed)
      .map(e => e.number);
    setCollapsedEnds(new Set(allCompletedEnds));
  };

  // Handler for toggling end collapse state
  const toggleEndCollapse = (endNumber: number) => {
    setCollapsedEnds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(endNumber)) {
        newSet.delete(endNumber);
      } else {
        newSet.add(endNumber);
      }
      return newSet;
    });
  };

  // Handler for manually switching the hammer
  const handleSwitchHammer = (endNumber: number) => {
    if (!game) return;

    const updatedGame = { ...game };
    const end = updatedGame.ends.find(e => e.number === endNumber);
    
    if (end) {
      const currentHammer = getTeamWithHammer(endNumber);
      const newHammer = currentHammer === 'team1' ? 'team2' : 'team1';
      end.hammerOverride = newHammer;
    }

    setGame(updatedGame);
    saveGame(updatedGame);
  };

  // Handler for updating rock order
  const handleUpdateRockOrder = (newOrder: number[], playerAssignments?: number[]) => {
    if (!game) return;

    const updatedGame = rockOrderTeam === 'team1'
      ? { 
          ...game, 
          team1RockOrder: newOrder,
          team1PlayerAssignments: playerAssignments 
        }
      : { 
          ...game, 
          team2RockOrder: newOrder,
          team2PlayerAssignments: playerAssignments 
        };
    setGame(updatedGame);
    saveGame(updatedGame);
  };

  // Handler for team selection from dropdown (own saved teams)
  const handleTeam1Selected = (teamName: string) => {
    setTeam1Name(teamName);
    setShowTeam1Suggestions(false);

    // Find the saved team and populate players
    const savedTeam = savedTeams.find(t => t.name === teamName);
    if (savedTeam) {
      setTeam1Players(savedTeam.players.map(p => p.displayName));
      setTeam1Source(savedTeam.accountId ? { accountId: savedTeam.accountId, teamId: savedTeam.id } : null);
    }
  };

  const handleTeam2Selected = (teamName: string) => {
    setTeam2Name(teamName);
    setShowTeam2Suggestions(false);

    // Find the saved team and populate players
    const savedTeam = savedTeams.find(t => t.name === teamName);
    if (savedTeam) {
      setTeam2Players(savedTeam.players.map(p => p.displayName));
      setTeam2Source(savedTeam.accountId ? { accountId: savedTeam.accountId, teamId: savedTeam.id } : null);
    }
  };

  // Handler for picking a team from another program's search results
  const handleTeam1SelectedFromOtherProgram = (result: SavedTeamSearchResult) => {
    setTeam1Name(result.name);
    setTeam1Players(result.playerNames);
    setTeam1Source({ accountId: result.accountId, teamId: result.teamId });
    setShowTeam1Suggestions(false);
  };

  const handleTeam2SelectedFromOtherProgram = (result: SavedTeamSearchResult) => {
    setTeam2Name(result.name);
    setTeam2Players(result.playerNames);
    setTeam2Source({ accountId: result.accountId, teamId: result.teamId });
    setShowTeam2Suggestions(false);
  };

  // Get filtered suggestions for team names. Archived teams are excluded — they're
  // retired, not meant to be picked for a new game.
  const getTeam1Suggestions = () => {
    if (!team1Name.trim()) return [];
    return savedTeams.filter(team =>
      !team.archived && team.name.toLowerCase().includes(team1Name.toLowerCase())
    );
  };

  const getTeam2Suggestions = () => {
    if (!team2Name.trim()) return [];
    return savedTeams.filter(team =>
      !team.archived && team.name.toLowerCase().includes(team2Name.toLowerCase())
    );
  };

  // Check if team is new (not in saved teams) — an archived team's name doesn't
  // count as "taken", so a new active team can reuse it.
  const isTeam1New = () => {
    if (!team1Name.trim()) return false;
    const validPlayers = team1Players.filter(p => p.trim());
    if (validPlayers.length < 2) return false;
    return !savedTeams.some(t => !t.archived && t.name === team1Name.trim());
  };

  const isTeam2New = () => {
    if (!team2Name.trim()) return false;
    const validPlayers = team2Players.filter(p => p.trim());
    if (validPlayers.length < 2) return false;
    return !savedTeams.some(t => !t.archived && t.name === team2Name.trim());
  };

  // Save team handler. A team saved this way is just a byproduct of typing an
  // opponent's name into a game, not a deliberate "this is our real roster" action
  // like Team Management — so it defaults to private (generic) rather than
  // discoverable. It's still fully searchable in this program's own game setup;
  // Team Management's Edit form is where someone promotes it to an official,
  // cross-program-discoverable team if it turns out to be one.
  const handleSaveTeam1 = async () => {
    const validPlayers = team1Players.filter(p => p.trim());
    if (!team1Name.trim() || validPlayers.length < 2) return;

    const newTeam: SavedTeam = {
      id: Date.now().toString(),
      name: team1Name.trim(),
      players: validPlayers.map(displayName => ({ displayName })),
      createdAt: Date.now(),
      discoverable: false,
      archived: false,
    };

    try {
      await saveTeam(newTeam);
      setSavedTeams(await getTeams());
    } catch (error) {
      console.error('Failed to save team:', error);
    }
  };

  // See handleSaveTeam1 — same "generic, private by default" reasoning applies here.
  const handleSaveTeam2 = async () => {
    const validPlayers = team2Players.filter(p => p.trim());
    if (!team2Name.trim() || validPlayers.length < 2) return;

    const newTeam: SavedTeam = {
      id: Date.now().toString(),
      name: team2Name.trim(),
      players: validPlayers.map(displayName => ({ displayName })),
      createdAt: Date.now(),
      discoverable: false,
      archived: false,
    };

    try {
      await saveTeam(newTeam);
      setSavedTeams(await getTeams());
    } catch (error) {
      console.error('Failed to save team:', error);
    }
  };

  // Helper function to get hammer button classes based on rock color
  const getHammerButtonClasses = (rockColor: RockColor, isSelected: boolean) => {
    if (!isSelected) {
      return 'flex-1 px-4 py-2 rounded-md font-medium transition-colors bg-white/10 hover:bg-white/20 border border-white/20 text-white';
    }
    
    const colorClasses = {
      red: 'bg-red-600 text-white hover:bg-red-700',
      yellow: 'bg-yellow-500 text-white hover:bg-yellow-600',
      green: 'bg-green-600 text-white hover:bg-green-700',
      blue: 'bg-blue-600 text-white hover:bg-blue-700',
      white: 'bg-white/20 text-white hover:bg-white/30',
    };

    return `flex-1 px-4 py-2 rounded-md font-medium transition-colors ${colorClasses[rockColor]}`;
  };

  // Helper function to initialize Mixed Doubles rock positions
  const initializeMixedDoublesRocks = (endNumber: number) => {
    if (!game) return;
    
    // Check if it's Mixed Doubles (both teams have exactly 2 players)
    const isMixedDoubles = game.team1Players.length === 2 && game.team2Players.length === 2;
    if (!isMixedDoubles) return;
    
    // Check if this end already has rocks positioned
    if (rockPositionsByEnd[endNumber] && rockPositionsByEnd[endNumber].length > 0) return;
    
    // Check if this is the first shot of the end
    const currentEnd = game.ends.find(e => e.number === endNumber);
    if (!currentEnd || currentEnd.shots.length > 0) return;
    
    // Determine which team has hammer
    const teamWithHammer = getTeamWithHammer(endNumber);
    const teamWithoutHammer = teamWithHammer === 'team1' ? 'team2' : 'team1';
    
    // Initialize rocks array with positioned rocks
    const initialRocks: Rock[] = [];
    const rocksPerTeam = 6; // Mixed Doubles uses 6 rocks per team
    let id = 0;
    
    // Team 1 rocks - positioned on left side above hog line (default positions)
    for (let i = 0; i < rocksPerTeam; i++) {
      const x = 148 + i * 15;
      const y = 12;
      
      // If team1 is without hammer and this is the last rock (6th rock), position it at zone 2 (Mid Guard, center line)
      if (teamWithoutHammer === 'team1' && i === 5) {
        initialRocks.push({
          id: id++,
          team: 'team1',
          x: 293.5, // Center line of house
          y: 112, // Zone 2 (Mid Guard)
        });
      } 
      // If team1 has hammer and this is the last rock (6th rock), position it at zone 8 (Back Four, center line)
      else if (teamWithHammer === 'team1' && i === 5) {
        initialRocks.push({
          id: id++,
          team: 'team1',
          x: 293.5, // Center line of house
          y: 360, // Zone 8 (Back Four)
        });
      } else {
        initialRocks.push({
          id: id++,
          team: 'team1',
          x,
          y,
        });
      }
    }
    
    // Team 2 rocks - positioned on right side above hog line (default positions)
    for (let i = 0; i < rocksPerTeam; i++) {
      const x = 440 - i * 15;
      const y = 12;
      
      console.log(`Team 2 rock ${i}: teamWithoutHammer=${teamWithoutHammer}, teamWithHammer=${teamWithHammer}`);
      
      // If team2 is without hammer and this is the last rock (6th rock), position it at zone 2 (Mid Guard, center line)
      if (teamWithoutHammer === 'team2' && i === 5) {
        console.log('Adding team 2 guard at zone 2');
        initialRocks.push({
          id: id++,
          team: 'team2',
          x: 293.5, // Center line of house
          y: 112, // Zone 2 (Mid Guard)
        });
      }
      // If team2 has hammer and this is the last rock (6th rock), position it at zone 8 (Back Four, center line)
      else if (teamWithHammer === 'team2' && i === 5) {
        console.log('Adding team 2 back rock at zone 8');
        initialRocks.push({
          id: id++,
          team: 'team2',
          x: 293.5, // Center line of house
          y: 360, // Zone 8 (Back Four)
        });
      } else {
        console.log(`Adding team 2 default rock at x=${x}, y=${y}`);
        initialRocks.push({
          id: id++,
          team: 'team2',
          x,
          y,
        });
      }
    }
    
    // Set the initial rock positions for this end
    setRockPositionsByEnd(prev => ({
      ...prev,
      [endNumber]: initialRocks,
    }));
    
    // Debug: log the positioned rocks
    console.log('Mixed Doubles rocks initialized for end', endNumber);
    console.log('Team with hammer:', teamWithHammer);
    console.log('Team without hammer:', teamWithoutHammer);
    console.log('Initial rocks:', initialRocks.map(r => ({ id: r.id, team: r.team, x: r.x, y: r.y })));
  };

  if (showSetup) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-950 via-blue-900 to-slate-900 flex items-center justify-center p-4">
        <div className="bg-white/8 backdrop-blur-sm border border-white/15 rounded-2xl shadow-xl shadow-black/20 max-w-2xl w-full p-8 max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl font-bold text-white">New Game Setup</h1>
            <button
              onClick={() => navigate('/CurlingCodex')}
              className="flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/20 text-white px-3 py-1.5 rounded-lg transition-colors text-sm"
            >
              <X className="w-4 h-4" />
              Exit
            </button>
          </div>

          <div className="space-y-6">
            {/* Game Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="relative">
                <label className="block text-sm font-medium text-blue-200 mb-1">Curling Club</label>
                <input
                  type="text"
                  value={clubSearchText}
                  onChange={(e) => setClubSearchText(e.target.value)}
                  onFocus={() => setShowClubSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowClubSuggestions(false), 200)}
                  className="w-full bg-white/10 border border-white/20 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400/60 placeholder:text-blue-300/50"
                  placeholder="Type to search clubs..."
                />
                {showClubSuggestions && clubSearchText.trim() && (() => {
                  const filteredClubs = getAllClubs().filter(club =>
                    club.name.toLowerCase().includes(clubSearchText.toLowerCase()) ||
                    club.city.toLowerCase().includes(clubSearchText.toLowerCase()) ||
                    club.country.toLowerCase().includes(clubSearchText.toLowerCase())
                  );

                  return (
                    <div className="absolute z-10 w-full mt-1 bg-blue-950 border border-white/20 rounded-lg shadow-xl max-h-48 overflow-y-auto">
                      {filteredClubs.length === 0 && (
                        <div className="px-3 py-2 text-sm text-blue-300/70 italic">No matching clubs</div>
                      )}
                      {filteredClubs.map((club, index) => (
                        <button
                          key={index}
                          type="button"
                          onClick={() => {
                            const fullClubName = `${club.name} - ${club.city}, ${club.country}`;
                            setCurlingClub(fullClubName);
                            setClubSearchText(fullClubName);
                            setShowClubSuggestions(false);
                          }}
                          className="w-full text-left px-3 py-2 hover:bg-blue-500/20 transition-colors"
                        >
                          <div className="font-medium text-white">{club.name}</div>
                          <div className="text-xs text-blue-300">{club.city}, {club.country}</div>
                        </button>
                      ))}
                      <button
                        type="button"
                        onClick={() => setShowAddClubModal(true)}
                        className="w-full text-left px-3 py-2 hover:bg-blue-500/20 transition-colors text-blue-300"
                      >
                        <Plus className="w-4 h-4 mr-1 inline" />
                        Add New Club
                      </button>
                    </div>
                  );
                })()}
              </div>

              <div>
                <label className="block text-sm font-medium text-blue-200 mb-1">Sheet Number</label>
                <input
                  type="text"
                  value={sheetNumber}
                  onChange={(e) => setSheetNumber(e.target.value)}
                  className="w-full bg-white/10 border border-white/20 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400/60 placeholder:text-blue-300/50"
                  placeholder="e.g., Sheet A, 1, 2"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Team 1 */}
              <div className="space-y-4">
                <div className="relative">
                  <label className="block text-sm font-medium text-blue-200 mb-1">Team 1 Name</label>
                  <input
                    type="text"
                    value={team1Name}
                    onChange={(e) => {
                      setTeam1Name(e.target.value);
                      setTeam1Source(null);
                      setShowTeam1Suggestions(true);
                    }}
                    onFocus={() => setShowTeam1Suggestions(true)}
                    onBlur={() => setTimeout(() => setShowTeam1Suggestions(false), 200)}
                    className="w-full bg-white/10 border border-white/20 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400/60 placeholder:text-blue-300/50"
                    placeholder="Enter team name"
                  />
                  {showTeam1Suggestions && (getTeam1Suggestions().length > 0 || otherProgramResults1.length > 0) && (
                    <div className="absolute z-10 w-full mt-1 bg-blue-950 border border-white/20 rounded-lg shadow-xl max-h-64 overflow-y-auto">
                      {getTeam1Suggestions().length > 0 && (
                        <div className="px-3 pt-2 pb-1 text-xs uppercase tracking-wider text-blue-300/60">
                          Your teams
                        </div>
                      )}
                      {getTeam1Suggestions().map(team => (
                        <button
                          key={team.id}
                          type="button"
                          onClick={() => handleTeam1Selected(team.name)}
                          className="w-full flex items-center gap-2 text-left px-3 py-2 hover:bg-blue-500/20 transition-colors"
                        >
                          {team.discoverable ? (
                            <Globe className="w-3.5 h-3.5 text-blue-300 flex-shrink-0" />
                          ) : (
                            <EyeOff className="w-3.5 h-3.5 text-blue-300/40 flex-shrink-0" />
                          )}
                          <span className={team.discoverable ? 'font-medium text-white' : 'font-medium text-blue-100/70'}>
                            {team.name}
                          </span>
                        </button>
                      ))}
                      {otherProgramResults1.length > 0 && (
                        <>
                          <div className="px-3 pt-2 pb-1 text-xs uppercase tracking-wider text-violet-300/70 border-t border-white/10">
                            Other programs
                          </div>
                          {otherProgramResults1.map(result => (
                            <button
                              key={`${result.accountId}-${result.teamId}`}
                              type="button"
                              onClick={() => handleTeam1SelectedFromOtherProgram(result)}
                              className="w-full flex items-center gap-2 text-left px-3 py-2 hover:bg-violet-500/20 transition-colors"
                            >
                              <Building2 className="w-3.5 h-3.5 text-violet-300 flex-shrink-0" />
                              <div>
                                <div className="font-medium text-white">{result.name}</div>
                                <div className="text-xs text-violet-300">{result.programName}</div>
                              </div>
                            </button>
                          ))}
                        </>
                      )}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-blue-200 mb-2">Team 1 Players</label>
                  <div className="space-y-2">
                    {team1Players.map((player, index) => (
                      <div key={index} className="flex gap-2">
                        <input
                          ref={(el) => (team1InputRefs.current[index] = el)}
                          type="text"
                          value={player}
                          onChange={(e) => {
                            const newPlayers = [...team1Players];
                            newPlayers[index] = e.target.value;
                            setTeam1Players(newPlayers);
                          }}
                          className="flex-1 bg-white/10 border border-white/20 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400/60 placeholder:text-blue-300/50"
                          placeholder={`Player ${index + 1}`}
                        />
                        {team1Players.length > 2 && (
                          <button
                            type="button"
                            onClick={() => setTeam1Players(team1Players.filter((_, i) => i !== index))}
                            disabled={team1Players.length <= 2}
                            className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => {
                        setTeam1Players([...team1Players, '']);
                        setTimeout(() => {
                          team1InputRefs.current[team1Players.length]?.focus();
                        }, 0);
                      }}
                      disabled={team1Players.length >= 5}
                      className="w-full flex items-center justify-center gap-2 px-3 py-2 border-2 border-dashed border-white/20 rounded-lg hover:border-blue-400/60 hover:bg-blue-500/10 transition-colors text-sm text-blue-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-white/20 disabled:hover:bg-transparent"
                    >
                      <UserPlus className="w-4 h-4" />
                      Add Player
                    </button>
                    {isTeam1New() && (
                      <>
                        <button
                          type="button"
                          onClick={handleSaveTeam1}
                          className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-blue-500 hover:bg-blue-400 text-white rounded-lg transition-colors"
                        >
                          <Save className="w-4 h-4" />
                          Save Team
                        </button>
                        <p className="text-xs text-blue-300/60">
                          Saved privately for your own games — manage it under Program
                          Management to make it discoverable to other programs.
                        </p>
                      </>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-blue-200 mb-1">Team 1 Rock Color</label>
                  <select
                    value={team1RockColor}
                    onChange={(e) => setTeam1RockColor(e.target.value as RockColor)}
                    className="w-full bg-white/10 border border-white/20 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400/60 font-medium"
                  >
                    <option value="red" className="bg-blue-950">Red</option>
                    <option value="yellow" className="bg-blue-950">Yellow</option>
                    <option value="green" className="bg-blue-950">Green</option>
                    <option value="blue" className="bg-blue-950">Blue</option>
                    <option value="white" className="bg-blue-950">White</option>
                  </select>
                </div>
              </div>

              {/* Team 2 */}
              <div className="space-y-4">
                <div className="relative">
                  <label className="block text-sm font-medium text-blue-200 mb-1">Team 2 Name</label>
                  <input
                    type="text"
                    value={team2Name}
                    onChange={(e) => {
                      setTeam2Name(e.target.value);
                      setTeam2Source(null);
                      setShowTeam2Suggestions(true);
                    }}
                    onFocus={() => setShowTeam2Suggestions(true)}
                    onBlur={() => setTimeout(() => setShowTeam2Suggestions(false), 200)}
                    className="w-full bg-white/10 border border-white/20 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400/60 placeholder:text-blue-300/50"
                    placeholder="Enter team name"
                  />
                  {showTeam2Suggestions && (getTeam2Suggestions().length > 0 || otherProgramResults2.length > 0) && (
                    <div className="absolute z-10 w-full mt-1 bg-blue-950 border border-white/20 rounded-lg shadow-xl max-h-64 overflow-y-auto">
                      {getTeam2Suggestions().length > 0 && (
                        <div className="px-3 pt-2 pb-1 text-xs uppercase tracking-wider text-blue-300/60">
                          Your teams
                        </div>
                      )}
                      {getTeam2Suggestions().map(team => (
                        <button
                          key={team.id}
                          type="button"
                          onClick={() => handleTeam2Selected(team.name)}
                          className="w-full flex items-center gap-2 text-left px-3 py-2 hover:bg-blue-500/20 transition-colors"
                        >
                          {team.discoverable ? (
                            <Globe className="w-3.5 h-3.5 text-blue-300 flex-shrink-0" />
                          ) : (
                            <EyeOff className="w-3.5 h-3.5 text-blue-300/40 flex-shrink-0" />
                          )}
                          <span className={team.discoverable ? 'font-medium text-white' : 'font-medium text-blue-100/70'}>
                            {team.name}
                          </span>
                        </button>
                      ))}
                      {otherProgramResults2.length > 0 && (
                        <>
                          <div className="px-3 pt-2 pb-1 text-xs uppercase tracking-wider text-violet-300/70 border-t border-white/10">
                            Other programs
                          </div>
                          {otherProgramResults2.map(result => (
                            <button
                              key={`${result.accountId}-${result.teamId}`}
                              type="button"
                              onClick={() => handleTeam2SelectedFromOtherProgram(result)}
                              className="w-full flex items-center gap-2 text-left px-3 py-2 hover:bg-violet-500/20 transition-colors"
                            >
                              <Building2 className="w-3.5 h-3.5 text-violet-300 flex-shrink-0" />
                              <div>
                                <div className="font-medium text-white">{result.name}</div>
                                <div className="text-xs text-violet-300">{result.programName}</div>
                              </div>
                            </button>
                          ))}
                        </>
                      )}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-blue-200 mb-2">Team 2 Players</label>
                  <div className="space-y-2">
                    {team2Players.map((player, index) => (
                      <div key={index} className="flex gap-2">
                        <input
                          ref={(el) => (team2InputRefs.current[index] = el)}
                          type="text"
                          value={player}
                          onChange={(e) => {
                            const newPlayers = [...team2Players];
                            newPlayers[index] = e.target.value;
                            setTeam2Players(newPlayers);
                          }}
                          className="flex-1 bg-white/10 border border-white/20 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400/60 placeholder:text-blue-300/50"
                          placeholder={`Player ${index + 1}`}
                        />
                        {team2Players.length > 2 && (
                          <button
                            type="button"
                            onClick={() => setTeam2Players(team2Players.filter((_, i) => i !== index))}
                            disabled={team2Players.length <= 2}
                            className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => {
                        setTeam2Players([...team2Players, '']);
                        setTimeout(() => {
                          team2InputRefs.current[team2Players.length]?.focus();
                        }, 0);
                      }}
                      disabled={team2Players.length >= 5}
                      className="w-full flex items-center justify-center gap-2 px-3 py-2 border-2 border-dashed border-white/20 rounded-lg hover:border-blue-400/60 hover:bg-blue-500/10 transition-colors text-sm text-blue-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-white/20 disabled:hover:bg-transparent"
                    >
                      <UserPlus className="w-4 h-4" />
                      Add Player
                    </button>
                    {isTeam2New() && (
                      <>
                        <button
                          type="button"
                          onClick={handleSaveTeam2}
                          className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-blue-500 hover:bg-blue-400 text-white rounded-lg transition-colors"
                        >
                          <Save className="w-4 h-4" />
                          Save Team
                        </button>
                        <p className="text-xs text-blue-300/60">
                          Saved privately for your own games — manage it under Program
                          Management to make it discoverable to other programs.
                        </p>
                      </>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-blue-200 mb-1">Team 2 Rock Color</label>
                  <select
                    value={team2RockColor}
                    onChange={(e) => setTeam2RockColor(e.target.value as RockColor)}
                    className="w-full bg-white/10 border border-white/20 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400/60 font-medium"
                  >
                    <option value="red" className="bg-blue-950">Red</option>
                    <option value="yellow" className="bg-blue-950">Yellow</option>
                    <option value="green" className="bg-blue-950">Green</option>
                    <option value="blue" className="bg-blue-950">Blue</option>
                    <option value="white" className="bg-blue-950">White</option>
                  </select>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-blue-200 mb-1">Number of Ends</label>
              <input
                type="number"
                value={numberOfEnds}
                onChange={(e) => setNumberOfEnds(parseInt(e.target.value) || 10)}
                className="w-full bg-white/10 border border-white/20 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400/60"
                min="1"
                max="20"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-blue-200 mb-2">Starts with Hammer</label>
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => setStartsWithHammer('team1')}
                  className={getHammerButtonClasses(team1RockColor, startsWithHammer === 'team1')}
                >
                  Team 1
                </button>
                <button
                  type="button"
                  onClick={() => setStartsWithHammer('team2')}
                  className={getHammerButtonClasses(team2RockColor, startsWithHammer === 'team2')}
                >
                  Team 2
                </button>
                <button
                  type="button"
                  onClick={() => setStartsWithHammer('lsd')}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    startsWithHammer === 'lsd'
                      ? 'bg-orange-500 text-white'
                      : 'bg-white/10 hover:bg-white/20 border border-white/20 text-white'
                  }`}
                >
                  Last Stone Draw
                </button>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-white mb-3">Default Times</h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-blue-200 mb-1">Tee Line (sec)</label>
                  <input
                    type="number"
                    step="0.05"
                    value={expectedTimeTeeLine.toFixed(2)}
                    onChange={(e) => setExpectedTimeTeeLine(parseFloat(e.target.value) || 14.5)}
                    className="w-full bg-white/10 border border-white/20 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400/60"
                    min="0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-blue-200 mb-1">Board (sec)</label>
                  <input
                    type="number"
                    step="0.05"
                    value={expectedTimeBoard.toFixed(2)}
                    onChange={(e) => setExpectedTimeBoard(parseFloat(e.target.value) || 6.0)}
                    className="w-full bg-white/10 border border-white/20 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400/60"
                    min="0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-blue-200 mb-1">Control (sec)</label>
                  <input
                    type="number"
                    step="0.05"
                    value={expectedTimeControl.toFixed(2)}
                    onChange={(e) => setExpectedTimeControl(parseFloat(e.target.value) || 7.0)}
                    className="w-full bg-white/10 border border-white/20 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400/60"
                    min="0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-blue-200 mb-1">Normal (sec)</label>
                  <input
                    type="number"
                    step="0.05"
                    value={expectedTimeNormal.toFixed(2)}
                    onChange={(e) => setExpectedTimeNormal(parseFloat(e.target.value) || 8.0)}
                    className="w-full bg-white/10 border border-white/20 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400/60"
                    min="0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-blue-200 mb-1">Firm (sec)</label>
                  <input
                    type="number"
                    step="0.05"
                    value={expectedTimeFirm.toFixed(2)}
                    onChange={(e) => setExpectedTimeFirm(parseFloat(e.target.value) || 9.0)}
                    className="w-full bg-white/10 border border-white/20 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400/60"
                    min="0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-blue-200 mb-1">Peel (sec)</label>
                  <input
                    type="number"
                    step="0.05"
                    value={expectedTimePeel.toFixed(2)}
                    onChange={(e) => setExpectedTimePeel(parseFloat(e.target.value) || 7.0)}
                    className="w-full bg-white/10 border border-white/20 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400/60"
                    min="0"
                  />
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-white mb-3">Default Curl</h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-blue-200 mb-1">Curl (ft)</label>
                  <input
                    type="number"
                    step="0.25"
                    value={defaultExpectedCurl.toFixed(2)}
                    onChange={(e) => setDefaultExpectedCurl(parseFloat(e.target.value) || 4.75)}
                    className="w-full bg-white/10 border border-white/20 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400/60"
                    min="0"
                  />
                </div>
              </div>
            </div>

            <button
              onClick={handleStartGame}
              disabled={!team1Name.trim() || !team2Name.trim() || !curlingClub.trim() || !sheetNumber.trim()}
              className="w-full bg-blue-500 hover:bg-blue-400 text-white py-3 rounded-lg disabled:bg-white/10 disabled:text-white/40 disabled:cursor-not-allowed transition-colors"
            >
              Start Game
            </button>
          </div>
        </div>

        {/* Add Club Modal */}
        {showAddClubModal && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
            <div className="bg-blue-950 border border-white/15 rounded-2xl shadow-xl shadow-black/20 max-w-md w-full p-6">
              <h2 className="text-xl font-bold text-white mb-4">Add New Curling Club</h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-blue-200 mb-1">Club Name</label>
                  <input
                    type="text"
                    value={newClubName}
                    onChange={(e) => setNewClubName(e.target.value)}
                    className="w-full bg-white/10 border border-white/20 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400/60 placeholder:text-blue-300/50"
                    placeholder="Enter club name"
                    autoFocus
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-blue-200 mb-1">City</label>
                  <input
                    type="text"
                    value={newClubCity}
                    onChange={(e) => setNewClubCity(e.target.value)}
                    className="w-full bg-white/10 border border-white/20 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400/60 placeholder:text-blue-300/50"
                    placeholder="Enter city"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-blue-200 mb-1">Country</label>
                  <input
                    type="text"
                    value={newClubCountry}
                    onChange={(e) => setNewClubCountry(e.target.value)}
                    className="w-full bg-white/10 border border-white/20 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400/60 placeholder:text-blue-300/50"
                    placeholder="Enter country"
                  />
                </div>

                <div className="flex gap-3 mt-6">
                  <button
                    onClick={() => {
                      setNewClubName('');
                      setNewClubCity('');
                      setNewClubCountry('');
                      setShowAddClubModal(false);
                    }}
                    className="flex-1 px-4 py-2 bg-white/10 hover:bg-white/20 border border-white/20 text-white rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddNewClub}
                    className="flex-1 px-4 py-2 bg-blue-500 hover:bg-blue-400 text-white rounded-lg transition-colors"
                  >
                    Add Club
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (!game) return null;

  // Helper function to determine which team has the hammer for a given end
  const getTeamWithHammer = (endNumber: number): 'team1' | 'team2' => {
    // Check if there's a manual override for this end
    const currentEnd = game.ends.find(e => e.number === endNumber);
    if (currentEnd?.hammerOverride) {
      return currentEnd.hammerOverride;
    }
    
    // First end uses the initial hammer setting with priority order:
    // 1. LSD hammer team from practice shots
    // 2. startsWithHammer from game setup
    // 3. Default to team1
    if (endNumber === 1) {
      const lsdHammerTeam = localStorage.getItem(`lsdHammerTeam_${gameId}`);
      if (lsdHammerTeam === 'team1' || lsdHammerTeam === 'team2') {
        return lsdHammerTeam;
      }
      return game.startsWithHammer || 'team1';
    }
    
    // For subsequent ends, the team that didn't score in the previous end gets the hammer
    const previousEnd = game.ends.find(e => e.number === endNumber - 1);
    if (!previousEnd) {
      const lsdHammerTeam = localStorage.getItem(`lsdHammerTeam_${gameId}`);
      if (lsdHammerTeam === 'team1' || lsdHammerTeam === 'team2') {
        return lsdHammerTeam;
      }
      return game.startsWithHammer || 'team1';
    }
    
    // Check if it's two-player curling (both teams have exactly 2 players)
    const isTwoPlayerCurling = game.team1Players.length === 2 && game.team2Players.length === 2;
    
    // If previous end was blank (0-0)
    if (previousEnd.team1Score === 0 && previousEnd.team2Score === 0) {
      if (isTwoPlayerCurling) {
        // In two-player curling, hammer switches on blank ends
        const previousHammer = getTeamWithHammer(endNumber - 1);
        return previousHammer === 'team1' ? 'team2' : 'team1';
      } else {
        // In regular curling, hammer stays with the same team on blank ends
        return getTeamWithHammer(endNumber - 1);
      }
    }
    
    // Team that scored loses the hammer (other team gets it)
    if (previousEnd.team1Score > previousEnd.team2Score) {
      return 'team2';
    } else if (previousEnd.team2Score > previousEnd.team1Score) {
      return 'team1';
    }
    
    // Fallback (shouldn't happen if scored properly)
    return getTeamWithHammer(endNumber - 1);
  };

  // Helper function to determine which team should shoot next in an end
  const getNextTeam = (end: End): 'team1' | 'team2' | 'either' => {
    // For the first shot of an end, the team WITHOUT the hammer shoots first
    if (end.shots.length === 0) {
      const teamWithHammer = getTeamWithHammer(end.number);
      return teamWithHammer === 'team1' ? 'team2' : 'team1';
    }
    
    // After first shot, alternate between teams
    const lastShot = end.shots[end.shots.length - 1];
    return lastShot.team === 'team1' ? 'team2' : 'team1';
  };

  // Helper function to determine if an end is currently active (unlocked)
  const isEndActive = (endNumber: number): boolean => {
    // First end is always active
    if (endNumber === 1) return true;
    
    // Check if previous end is completed
    const previousEnd = game.ends.find(e => e.number === endNumber - 1);
    return previousEnd?.completed === true;
  };

  // Calculate weight counts for each team
  const getWeightCounts = (team: 'team1' | 'team2') => {
    let heavy = 0;
    let light = 0;
    let medium = 0;

    game.ends.forEach(end => {
      end.shots.forEach(shot => {
        if (shot.team === team && !shot.skipped) {
          if (shot.weight === 'heavy') heavy++;
          else if (shot.weight === 'light') light++;
          else if (shot.weight === 'good') medium++;
        }
      });
    });

    return { heavy, light, medium };
  };

  // Calculate gold star and bomb counts for each team
  const getSpecialCounts = (team: 'team1' | 'team2') => {
    let goldStars = 0;
    let bombs = 0;

    game.ends.forEach(end => {
      end.shots.forEach(shot => {
        if (shot.team === team && !shot.skipped) {
          if (shot.isGoldStar) goldStars++;
          if (shot.isBomb) bombs++;
        }
      });
    });

    return { goldStars, bombs };
  };

  // Helper function to get button classes based on rock color
  const getButtonClasses = (rockColor: RockColor | undefined, isDisabled: boolean) => {
    if (isDisabled) {
      return 'flex items-center gap-2 bg-gray-100 text-gray-400 border border-gray-200 px-4 py-2 rounded-lg cursor-not-allowed';
    }

    const colorClasses: Record<RockColor, string> = {
      red: 'bg-red-600 text-white hover:bg-red-700',
      yellow: 'bg-yellow-500 text-white hover:bg-yellow-600',
      green: 'bg-green-600 text-white hover:bg-green-700',
      blue: 'bg-blue-600 text-white hover:bg-blue-700',
      white: 'bg-gray-200 text-gray-800 hover:bg-gray-300',
    };

    const colorClass = rockColor
      ? colorClasses[rockColor]
      : 'bg-gray-200 text-gray-800 hover:bg-gray-300';

    return `flex items-center gap-2 ${colorClass} px-4 py-2 rounded-lg transition-colors`;
  };

  // Helper function to get scoreboard classes based on rock color
  // Rock colours are optional on a game, so these fall back to neutral styling.
  // Indexing with an undefined colour previously yielded undefined, which was then
  // interpolated into className as the literal string "undefined".
  const getScoreboardClasses = (rockColor: RockColor | undefined) => {
    const colorClasses: Record<RockColor, string> = {
      red: 'bg-red-50',
      yellow: 'bg-yellow-50',
      green: 'bg-emerald-50',
      blue: 'bg-blue-50',
      white: 'bg-gray-100',
    };
    return rockColor ? colorClasses[rockColor] : 'bg-gray-100';
  };

  // Helper function to get score text classes based on rock color
  const getScoreTextClasses = (rockColor: RockColor | undefined) => {
    const colorClasses: Record<RockColor, string> = {
      red: 'text-red-600',
      yellow: 'text-yellow-600',
      green: 'text-green-600',
      blue: 'text-blue-600',
      white: 'text-gray-800',
    };
    return rockColor ? colorClasses[rockColor] : 'text-gray-800';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Completed Game Banner */}
        {game.isComplete && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 mb-6 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Eye className="w-6 h-6 text-emerald-300" />
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Viewing Completed Game</h2>
                <p className="text-sm text-emerald-600">This game is read-only and cannot be edited.</p>
              </div>
            </div>
            <button
              onClick={() => navigate('/CurlingCodex/history')}
              className="flex items-center gap-2 bg-emerald-500/80 hover:bg-emerald-500 text-white px-4 py-2 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to History
            </button>
          </div>
        )}

        {/* Header */}
        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{game.isComplete ? 'Game Summary' : 'Live Game Summary'}</h1>
              {(game.curlingClub || game.sheetNumber) && (
                <div className="flex gap-3 mt-1 text-sm text-gray-500">
                  {game.curlingClub && <span>{game.curlingClub}</span>}
                  {game.curlingClub && game.sheetNumber && <span>•</span>}
                  {game.sheetNumber && <span>Sheet {game.sheetNumber}</span>}
                </div>
              )}
            </div>
            {!game.isComplete && (
              <div className="flex gap-2">
                <button
                  onClick={() => navigate('/CurlingCodex')}
                  className="flex items-center gap-2 bg-gray-100 hover:bg-gray-200 border border-gray-300 text-gray-700 px-4 py-2 rounded-lg transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Leave Game
                </button>
                <button
                  onClick={handleCompleteGame}
                  className="flex items-center gap-2 bg-emerald-500/80 hover:bg-emerald-500 text-white px-4 py-2 rounded-lg transition-colors"
                >
                  <Flag className="w-4 h-4" />
                  Complete Game
                </button>
              </div>
            )}
          </div>

          {/* Scoreboard */}
          <div className="grid grid-cols-1 gap-4">
            <div className={getScoreboardClasses(game.team1RockColor) + ' p-6 rounded-xl border border-gray-200'}>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">{game.team1Name}</h2>
              <p className={getScoreTextClasses(game.team1RockColor) + ' text-4xl font-bold mb-3'}>{game.team1TotalScore}</p>
              <div className="flex gap-3 text-sm flex-wrap">
                <div className="flex items-center gap-1">
                  <span className="font-medium text-gray-600">Heavy:</span>
                  <span className="px-2 py-1 bg-red-100 text-red-700 rounded font-semibold">{getWeightCounts('team1').heavy}</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="font-medium text-gray-600">Light:</span>
                  <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded font-semibold">{getWeightCounts('team1').light}</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="font-medium text-gray-600">Gold Stars:</span>
                  <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded font-semibold">{getSpecialCounts('team1').goldStars}</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="font-medium text-gray-600">Bombs:</span>
                  <span className="px-2 py-1 bg-gray-200 text-gray-700 rounded font-semibold">{getSpecialCounts('team1').bombs}</span>
                </div>
              </div>
            </div>
            <div className={getScoreboardClasses(game.team2RockColor) + ' p-6 rounded-xl border border-gray-200'}>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">{game.team2Name}</h2>
              <p className={getScoreTextClasses(game.team2RockColor) + ' text-4xl font-bold mb-3'}>{game.team2TotalScore}</p>
              <div className="flex gap-3 text-sm flex-wrap">
                <div className="flex items-center gap-1">
                  <span className="font-medium text-gray-600">Heavy:</span>
                  <span className="px-2 py-1 bg-red-100 text-red-700 rounded font-semibold">{getWeightCounts('team2').heavy}</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="font-medium text-gray-600">Light:</span>
                  <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded font-semibold">{getWeightCounts('team2').light}</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="font-medium text-gray-600">Gold Stars:</span>
                  <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded font-semibold">{getSpecialCounts('team2').goldStars}</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="font-medium text-gray-600">Bombs:</span>
                  <span className="px-2 py-1 bg-gray-200 text-gray-700 rounded font-semibold">{getSpecialCounts('team2').bombs}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Map Links */}
          <div className="mt-4 flex gap-3">
            <button
              onClick={() => navigate(`/CurlingCodex/game/${gameId}/player-summary`)}
              className="flex items-center gap-2 text-blue-600 hover:text-blue-800 hover:bg-gray-100 px-4 py-2 rounded-lg transition-colors"
            >
              <Users className="w-4 h-4" />
              Player Summary
            </button>
            <button
              onClick={() => navigate(`/CurlingCodex/game/${gameId}/sheet-map`)}
              className="flex items-center gap-2 text-blue-600 hover:text-blue-800 hover:bg-gray-100 px-4 py-2 rounded-lg transition-colors"
            >
              <MapPin className="w-4 h-4" />
              View Sheet Map
            </button>
            <button
              onClick={() => navigate(`/CurlingCodex/game/${gameId}/rock-map`)}
              className="flex items-center gap-2 text-blue-600 hover:text-blue-800 hover:bg-gray-100 px-4 py-2 rounded-lg transition-colors"
            >
              <Map className="w-4 h-4" />
              View Rock Map
            </button>
            <button
              onClick={() => navigate(`/CurlingCodex/game/${gameId}/brc-scoring`)}
              className="flex items-center gap-2 text-blue-600 hover:text-blue-800 hover:bg-gray-100 px-4 py-2 rounded-lg transition-colors"
            >
              <Gauge className="w-4 h-4" />
              BRC Scoring
            </button>
          </div>
        </div>

        {/* Ends */}
        <div className="space-y-6">
          {/* Practice Shots Section */}
          <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Practice Shots</h3>
            </div>
            <p className="text-gray-500 mb-4">
              Record practice shots before the game begins to determine the Last Stone Draw (LSD) and get familiar with ice conditions.
            </p>
            <button
              onClick={() => navigate(`/CurlingCodex/game/${gameId}/practice`)}
              disabled={game.ends.some(end => end.shots.length > 0)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                game.ends.some(end => end.shots.length > 0)
                  ? 'bg-white/10 text-white/40 cursor-not-allowed'
                  : 'bg-blue-500 hover:bg-blue-400 text-white'
              }`}
            >
              <Target className="w-4 h-4" />
              Record Practice Shots
            </button>
          </div>

          {game.ends.map(end => {
            const endIsActive = isEndActive(end.number);
            const endIsCompleted = end.completed === true;

            return (
            <div
              key={end.number}
              className={`bg-white border border-gray-200 rounded-2xl shadow-sm p-6 relative ${!endIsActive ? 'opacity-60' : ''}`}
            >
              {/* Locked overlay for inactive ends */}
              {!endIsActive && (
                <div className="absolute inset-0 bg-gray-100/80 rounded-2xl flex items-center justify-center z-10">
                  <div className="text-center">
                    <Lock className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                    <p className="text-gray-500 font-medium">Complete End {end.number - 1} to unlock</p>
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-semibold text-gray-900">End {end.number}</h3>
                    {endIsCompleted && (
                      <button
                        onClick={() => toggleEndCollapse(end.number)}
                        className="p-1 hover:bg-gray-100 rounded transition-colors"
                        title={collapsedEnds.has(end.number) ? "Expand" : "Collapse"}
                      >
                        {collapsedEnds.has(end.number) ? (
                          <ChevronDown className="w-5 h-5 text-gray-500" />
                        ) : (
                          <ChevronUp className="w-5 h-5 text-gray-500" />
                        )}
                      </button>
                    )}
                  </div>
                  {endIsActive && !endIsCompleted && (
                    <>
                      <button
                        onClick={() => {
                          setRockOrderTeam('team1');
                          setShowRockOrderPopup(true);
                        }}
                        className="flex items-center gap-2 bg-blue-50 text-blue-600 px-3 py-2 rounded-lg hover:bg-blue-100 transition-colors text-sm"
                      >
                        <List className="w-4 h-4" />
                        {game.team1Name} Order
                      </button>
                      <button
                        onClick={() => {
                          setRockOrderTeam('team2');
                          setShowRockOrderPopup(true);
                        }}
                        className="flex items-center gap-2 bg-gray-100 hover:bg-gray-200 border border-gray-300 text-gray-700 px-3 py-2 rounded-lg transition-colors text-sm"
                      >
                        <List className="w-4 h-4" />
                        {game.team2Name} Order
                      </button>
                    </>
                  )}
                  {/* Completion badge */}
                  {endIsCompleted && (
                    <div className="flex items-center gap-2 bg-emerald-50 text-emerald-700 px-3 py-1 rounded-full border border-emerald-200">
                      <CheckCircle2 className="w-4 h-4" />
                      <span className="text-sm font-medium">Completed</span>
                    </div>
                  )}
                </div>
                <div className="flex gap-4 items-center">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-600">{game.team1Name}:</span>
                    <input
                      type="number"
                      value={end.team1Score}
                      onChange={(e) => handleEndScoreUpdate(end.number, 'team1', parseInt(e.target.value) || 0)}
                      disabled={!endIsActive || game.isComplete}
                      className="w-16 px-2 py-1 bg-white border border-gray-300 text-gray-900 rounded text-center disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-400"
                      min="0"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-600">{game.team2Name}:</span>
                    <input
                      type="number"
                      value={end.team2Score}
                      onChange={(e) => handleEndScoreUpdate(end.number, 'team2', parseInt(e.target.value) || 0)}
                      disabled={!endIsActive || game.isComplete}
                      className="w-16 px-2 py-1 bg-white border border-gray-300 text-gray-900 rounded text-center disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-400"
                      min="0"
                    />
                  </div>
                </div>
              </div>

              {/* Add Shot Buttons */}
              {endIsActive && !endIsCompleted && (
              <div className="flex justify-between gap-2 mb-4">
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      initializeMixedDoublesRocks(end.number);
                      setSelectedTeam('team1');
                      setSelectedEndNumber(end.number);
                      setShowShotTracker(true);
                    }}
                    disabled={getNextTeam(end) === 'team2'}
                    className={getButtonClasses(game.team1RockColor, getNextTeam(end) === 'team2')}
                  >
                    <Plus className="w-4 h-4" />
                    Add Shot - {game.team1Name}
                  </button>
                  <button
                    onClick={() => {
                      initializeMixedDoublesRocks(end.number);
                      setSelectedTeam('team2');
                      setSelectedEndNumber(end.number);
                      setShowShotTracker(true);
                    }}
                    disabled={getNextTeam(end) === 'team1'}
                    className={getButtonClasses(game.team2RockColor, getNextTeam(end) === 'team1')}
                  >
                    <Plus className="w-4 h-4" />
                    Add Shot - {game.team2Name}
                  </button>
                </div>
                <div className="flex gap-2">
                  <SheetVisualizer game={game} />
                  <button
                    onClick={() => handleSwitchHammer(end.number)}
                    disabled={end.shots.length > 0}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                      end.shots.length > 0
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200'
                        : 'bg-gray-100 hover:bg-gray-200 border border-gray-300 text-gray-700'
                    }`}
                    title={`Hammer: ${getTeamWithHammer(end.number) === 'team1' ? game.team1Name : game.team2Name}`}
                  >
                    <Repeat2 className="w-4 h-4" />
                    Switch Hammer
                  </button>
                  <button
                    onClick={() => handleCompleteEnd(end.number)}
                    className="flex items-center gap-2 bg-blue-500 hover:bg-blue-400 text-white px-4 py-2 rounded-lg transition-colors"
                  >
                    <CheckCircle2 className="w-4 h-4" />
                    Complete End
                  </button>
                </div>
              </div>
              )}

              {/* Shots List */}
              {!collapsedEnds.has(end.number) && (
                <>
                  {end.shots.length > 0 ? (
                    <div className="space-y-2">
                      {end.shots.map(shot => {
                    const teamRockColor = shot.team === 'team1' ? game.team1RockColor : game.team2RockColor;
                    const bgColor = getScoreboardClasses(teamRockColor);
                    
                    return (
                    <div
                      key={shot.id}
                      className={`flex items-center justify-between p-3 rounded-lg ${bgColor}`}
                    >
                      <div className="flex-1">
                        {shot.skipped ? (
                          <div className="text-slate-400 italic">
                            Shot Skipped
                          </div>
                        ) : (
                          <div className="flex items-center gap-3 flex-wrap">
                            <span className="font-medium text-gray-900">{shot.playerName}</span>
                            <span className="text-sm font-semibold text-gray-700">
                              {getPointsFromResult(shot.result)} pts
                            </span>
                            <span className="text-sm text-gray-500 capitalize">
                              {shot.shotType}{shot.specialShot ? ':' : ''}
                            </span>
                            {shot.specialShot && (
                              <span className="text-sm text-purple-600 capitalize">
                                {shot.specialShot}
                              </span>
                            )}
                            <span className="text-sm text-gray-500">
                              → {getZoneLabel(shot.actualPosition)}
                            </span>
                            {shot.time > 0 && (
                              <span className="text-sm px-2 py-1 rounded bg-blue-50 text-blue-700 font-mono">
                                {shot.time.toFixed(2)}s
                              </span>
                            )}
                            {shot.weight !== 'good' && (
                              <span className={`text-sm px-2 py-1 rounded capitalize ${
                                shot.weight === 'heavy' ? 'bg-red-100 text-red-700' :
                                'bg-yellow-100 text-yellow-700'
                              }`}>
                                {shot.weight}
                              </span>
                            )}
                            {shot.curlAssessment && shot.curlAssessment !== 'good' && (
                              <span className={`text-sm px-2 py-1 rounded capitalize ${
                                shot.curlAssessment === 'over' ? 'bg-red-100 text-red-700' :
                                'bg-yellow-100 text-yellow-700'
                              }`}>
                                {shot.curlAssessment}
                              </span>
                            )}
                            {shot.isGoldStar && (
                              <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                            )}
                            {shot.isBomb && (
                              <Bomb className="w-5 h-5 text-red-500" />
                            )}
                            {shot.hasVisualizerPlan && <ShotPlanView shot={shot} />}
                            {shot.issue && shot.issue !== 'none' && (
                              <span className="text-sm px-2 py-1 rounded capitalize bg-orange-100 text-orange-700">
                                {shot.issue}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-gray-400 text-sm">No shots recorded yet</p>
              )}
                </>
              )}
            </div>
            );
          })}
        </div>
      </div>

      {showShotTracker && (() => {
        // Calculate current shot number for this team in this end
        const currentEnd = game.ends.find(e => e.number === selectedEndNumber);
        const shotCount = currentEnd 
          ? currentEnd.shots.filter(s => s.team === selectedTeam).length 
          : 0;
        
        return (
          <ShotTracker
            endNumber={selectedEndNumber}
            team={selectedTeam}
            teamName={selectedTeam === 'team1' ? game.team1Name : game.team2Name}
            playerNames={selectedTeam === 'team1' ? game.team1Players : game.team2Players}
            shotNumber={shotCount + 1}
            onShotRecorded={handleShotRecorded}
            onClose={() => setShowShotTracker(false)}
            expectedTimes={game.expectedTimes}
            team1Color={getRockColorHex(game.team1RockColor)}
            team2Color={getRockColorHex(game.team2RockColor)}
            game={game}
            initialRocks={rockPositionsByEnd[selectedEndNumber]}
            rockOrder={selectedTeam === 'team1' ? game.team1RockOrder : game.team2RockOrder}
            playerAssignments={selectedTeam === 'team1' ? game.team1PlayerAssignments : game.team2PlayerAssignments}
          />
        );
      })()}

      {/* Rock Order Popup */}
      {showRockOrderPopup && (
        <RockOrderPopup
          rockOrder={
            rockOrderTeam === 'team1' 
              ? (game.team1RockOrder || [1, 2, 3, 4, 5, 6, 7, 8])
              : (game.team2RockOrder || [1, 2, 3, 4, 5, 6, 7, 8])
          }
          playerAssignments={
            rockOrderTeam === 'team1'
              ? game.team1PlayerAssignments
              : game.team2PlayerAssignments
          }
          team={rockOrderTeam}
          teamName={rockOrderTeam === 'team1' ? game.team1Name : game.team2Name}
          players={rockOrderTeam === 'team1' ? game.team1Players : game.team2Players}
          rockColor={rockOrderTeam === 'team1' ? game.team1RockColor : game.team2RockColor}
          onSave={handleUpdateRockOrder}
          onClose={() => setShowRockOrderPopup(false)}
        />
      )}
    </div>
  );
}

// Rock Order Popup Component
function RockOrderPopup({ 
  rockOrder, 
  playerAssignments,
  team,
  teamName,
  players,
  rockColor,
  onSave, 
  onClose 
}: { 
  rockOrder: number[]; 
  playerAssignments?: number[];
  team: 'team1' | 'team2';
  teamName: string;
  players: string[];
  rockColor?: RockColor;
  onSave: (newOrder: number[], playerAssignments?: number[]) => void; 
  onClose: () => void; 
}) {
  const [order, setOrder] = useState<number[]>(rockOrder);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  // Initialize player assignments based on default pattern or saved assignments
  const getDefaultPlayerAssignments = (): number[] => {
    const playerCount = players.length;
    
    // Shot patterns based on player count
    const shotPatterns: Record<number, number[]> = {
      2: [0, 1, 1, 1, 0], // 5 shots for 2 players (positions 6-8 will be -1)
      3: [0, 0, 0, 1, 1, 1, 2, 2], // 8 shots for 3 players
      4: [0, 0, 1, 1, 2, 2, 3, 3], // 8 shots for 4 players
      5: [0, 0, 1, 1, 2, 2, 3, 3], // 8 shots for 4 players (5th is substitute)
    };
    
    const pattern = shotPatterns[playerCount] || [0, 0, 0, 0, 0, 0, 0, 0];
    
    // For 2-player teams, positions 6-8 are unassigned (-1)
    if (playerCount === 2) {
      return [...pattern, -1, -1, -1];
    }
    
    return pattern;
  };

  const [assignments, setAssignments] = useState<number[]>(
    playerAssignments || getDefaultPlayerAssignments()
  );

  // Helper function to get player name for a position
  const getPlayerName = (position: number): string => {
    const playerIndex = assignments[position - 1];
    
    if (playerIndex === -1) {
      return 'Unassigned';
    }
    
    const fullName = players[playerIndex] || players[0] || '';
    // Extract first name (everything before the first space)
    return fullName.split(' ')[0];
  };

  // Always show all 8 positions
  const positions = [1, 2, 3, 4, 5, 6, 7, 8];

  const handleRockClick = (index: number) => {
    if (selectedIndex === null) {
      // First selection - select this rock
      setSelectedIndex(index);
    } else if (selectedIndex === index) {
      // Clicking the same rock - deselect
      setSelectedIndex(null);
    } else {
      // Second selection - swap the rocks
      const newOrder = [...order];
      const temp = newOrder[selectedIndex];
      newOrder[selectedIndex] = newOrder[index];
      newOrder[index] = temp;

      setOrder(newOrder);
      setSelectedIndex(null);
    }
  };

  const handleSave = () => {
    onSave(order, assignments);
    onClose();
  };

  const handleReset = () => {
    setOrder([1, 2, 3, 4, 5, 6, 7, 8]);
    setAssignments(getDefaultPlayerAssignments());
  };

  const handlePlayerChange = (position: number, playerIndex: number) => {
    const newAssignments = [...assignments];
    newAssignments[position - 1] = playerIndex;
    setAssignments(newAssignments);
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-blue-950 border border-white/15 rounded-2xl shadow-xl shadow-black/20 max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-white">{teamName} - Rock Order</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors text-blue-300 hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-sm text-blue-300 mb-4">
          Select players for each position and drag rocks to reorder them.
        </p>

        <div className="space-y-2 mb-6">
          {positions.map((position, index) => {
            const rockNum = order[index];
            const isUnassigned = getPlayerName(position) === 'Unassigned';
            const playerIndex = assignments[position - 1];
            const isDisabled = players.length === 2 && position > 5;

            return (
              <div key={position} className="space-y-2">
                <div className="flex items-center gap-3">
                  <div className="text-sm text-blue-300 font-medium w-8 text-center">
                    {position}
                  </div>
                  <select
                    value={playerIndex}
                    onChange={(e) => handlePlayerChange(position, parseInt(e.target.value))}
                    disabled={isDisabled}
                    className={`px-3 py-2 bg-white/10 border border-white/20 text-white rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400/60 min-w-[140px] ${
                      isDisabled ? 'bg-white/5 text-white/30 cursor-not-allowed' : ''
                    }`}
                  >
                    {players.length === 2 && position > 5 ? (
                      <option value={-1} className="bg-blue-950">Unassigned</option>
                    ) : (
                      <>
                        {players.map((player, idx) => (
                          <option key={idx} value={idx} className="bg-blue-950">
                            {player.split(' ')[0]}
                          </option>
                        ))}
                      </>
                    )}
                  </select>
                  <div
                    onClick={() => handleRockClick(index)}
                    style={{
                      backgroundColor: isUnassigned
                        ? 'rgba(255,255,255,0.05)'
                        : rockColor ? `${getRockColorHex(rockColor)}20` : undefined,
                      borderColor: selectedIndex === index
                        ? '#60a5fa'
                        : (isUnassigned
                          ? 'rgba(255,255,255,0.15)'
                          : rockColor ? getRockColorHex(rockColor) : undefined),
                      borderWidth: selectedIndex === index ? '3px' : '2px',
                    }}
                    className={`flex-1 flex items-center justify-between p-3 border rounded-lg cursor-pointer transition-all ${
                      selectedIndex === index
                        ? 'shadow-lg ring-2 ring-blue-400'
                        : 'hover:shadow-md'
                    }`}
                  >
                    <span className="font-bold text-white">Rock #{rockNum}</span>
                    {selectedIndex === index && (
                      <div className="text-blue-300 font-bold">
                        Selected
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex gap-3">
          <button
            onClick={handleReset}
            className="flex-1 px-4 py-2 bg-white/10 hover:bg-white/20 border border-white/20 text-white rounded-lg transition-colors font-medium"
          >
            Reset to Default
          </button>
          <button
            onClick={handleSave}
            className="flex-1 px-4 py-2 bg-blue-500 hover:bg-blue-400 text-white rounded-lg transition-colors font-medium"
          >
            Save Order
          </button>
        </div>
      </div>
    </div>
  );
}